import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { AppConfig } from 'src/app/core/configs/app.config';
import { ChannelStatus } from 'src/app/models/channel';
import { Document } from 'src/app/models/document';
import { FileService } from 'src/app/services/file.service';
import { MediaPlanService } from 'src/app/services/media-plan.service';
import { LoadingOverlayService } from 'src/app/shared/loading-overlay/loading-overlay.service';
import {
    checkIfModeExistsInQueryParams,
    showChannelStatusDistinction,
    getProductEndDateByChannelId,
    isLessThan2WorkingDays
} from 'src/app/shared/utility/campaign';
import { WarningMessageModalComponent } from 'src/app/shared/warning-message-modal/warning-message-modal.component';
import { environment } from '../../../../environments/environment';
import { Campaign } from '../../../models/campaign';
import { CampaignHelperService } from '../../../services/campaign-helper.service';
import { CampaignService } from '../../../services/campaign.service';
import { CampaignMediaService } from '../../../services/campaign-media.service';
import { AlertService } from '../../../shared/alert/alert.service';
import { ConfirmCampaignCreationModalComponent } from '../confirm-campaign-creation-modal/confirm-campaign-creation-modal.component';
import { CampaignStepBase } from '../guards/campaign-step-base';
import { PauseCampaignVisualModalComponent } from './pause-campaign-visual-modal/pause-campaign-visual-modal.component';

// --- Injected Refactored Helpers ---
import { RulesHelper } from './rules-helper';
import { UploadService } from './upload.service';
import { MediaHelperService } from './media-helper.service';

@Component({
    selector: 'app-add-media',
    templateUrl: './add-media.component.html',
    styleUrls: ['./add-media.component.scss']
})
export class AddMediaComponent extends CampaignStepBase implements OnInit {

    public form: FormGroup;
    public uploadingMedia = false;
    public loading = false;
    public isFormSubmitted = false;
    public appConfig = AppConfig;
    public ChannelStatus = ChannelStatus;

    public previewMode = false;
    public partialEditMode = false;
    public liveEditMode = false;
    public editMode = false;

    public campaignId: string;
    public campaign: Campaign;
    public campaignChannels: any = [];
    public mediasList: any[] = [];
    public mediasData: any;
    public rulesArrayValues: any[] = [];
    public removedSubMedias: any[] = [];

    public numberOfVisuals: number;
    public newNumberOfVisuals: number = 0;

    public selectedFiles: File[] = [];
    public previousFiles: Document[] = [];
    public selectedFileType: 'excel' | 'pdf' | null = null;
    public hasExceededMaxFileSize: boolean = false;
    public documentsSizes = 0;
    public mediaUrl = environment.apiUrl;

    public uploadFilesTitle = 'Add your supporting documents (Trade License, D.E.D Permit, ...)';
    public subTitles = [
        'If your campaign is for a real estate, please make sure to upload the RERA Certificate with design',
        'If your campaign includes a price or a promotion, please make sure to upload the D.E.D permit'
    ];
    public acceptedFormats = '.doc,.docx,.png,.jpg,.jpeg';
    public maxFileSize = 50 * 1024 * 1024;

    @ViewChild('uploadImageDialog') uploadImageDialog: TemplateRef<any>;

    constructor(
        private formBuilder: FormBuilder,
        private campaignService: CampaignService,
        private campaignMediaService: CampaignMediaService,
        private campaignHelperService: CampaignHelperService,
        private alertService: AlertService,
        private fileService: FileService,
        private route: ActivatedRoute,
        public dialog: MatDialog,
        private campaignProposal: MediaPlanService,
        private loadingOverlayService: LoadingOverlayService,
        private authenticationService: AuthenticationService,
        private uploadService: UploadService,
        private mediaHelper: MediaHelperService,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    ngOnInit(): void {

        this.previewMode = checkIfModeExistsInQueryParams(this.route, 'preview');
        this.editMode = checkIfModeExistsInQueryParams(this.route, 'edit-mode');
        this.partialEditMode = checkIfModeExistsInQueryParams(this.route, 'partial-edit-mode');
        this.liveEditMode = checkIfModeExistsInQueryParams(this.route, 'live-edit-mode');

        this.getCampaign();
        this.initForm();
    }

    public isClient(): boolean {
        return this.authenticationService.isClient();
    }

    public isPreviewModeAndEditMode(): boolean {
        return this.previewMode && this.editMode;
    }

    private isReadOnlyFlow(): boolean {
        return (this.previewMode && !this.editMode) || !this.isClient();
    }

    public disableMediaButton(mediaStatus: string): boolean {
        if (this.previewMode && !this.editMode) return true;
        return ['APPROVED', 'PAUSED'].includes(mediaStatus);
    }

    public hideUploadMedia(mediaStatus: string): boolean {
        if (this.previewMode && !this.editMode) return false;
        return !['APPROVED', 'PAUSED'].includes(mediaStatus);
    }

    /**
     * Snapshot of everything step 4 owns, taken after a load or a successful save. Comparing
     * against it is what makes "has the client changed anything?" answerable — the previous
     * check only noticed brand-new complete visuals and removals, so editing a title, a
     * channel, a rule or the visual count left Save disabled.
     */
    private pristineSnapshot: string = null;

    private captureStepSnapshot(): string {
        return JSON.stringify({
            medias: this.mediasList,
            numberOfVisuals: this.numberOfVisuals,
            newNumberOfVisuals: this.newNumberOfVisuals,
            documents: (this.selectedFiles || []).map(f => `${f.name}:${f.size}`),
            previousDocuments: (this.previousFiles || []).map((f: any) => f.title)
        });
    }

    /** Marks the current state as saved, so Save goes back to disabled. */
    private markStepPristine(): void {
        this.pristineSnapshot = this.captureStepSnapshot();
    }

    public hasStepChanges(): boolean {
        if (this.pristineSnapshot === null) {
            return false;
        }
        return this.captureStepSnapshot() !== this.pristineSnapshot;
    }

    public shouldNotSaveCampaign(): boolean {
        return !this.hasStepChanges();
    }

    public isValidNumberOfVisuals(): boolean {
        return RulesHelper.isValidNumberOfVisuals(this.numberOfVisuals);
    }

    public isValidNewNumberOfVisuals(): boolean {
        return RulesHelper.isValidNewNumberOfVisuals(this.newNumberOfVisuals);
    }

    public checkFieldsValidation(): boolean {
        const areFieldsValid = RulesHelper.validateFields(this.campaign, this.mediasList);
        const isVisualCountValid = this.isValidNumberOfVisuals();
        const hasIncomleteVisual = RulesHelper.hasAnyIncompleteVisual(this.mediasList);
        const areStatusesValid = RulesHelper.allValidVisualStatuses(this.mediasList);

        let isNewCountValid = true;
        if (this.editMode || this.partialEditMode || this.liveEditMode) {
            isNewCountValid = this.isValidNewNumberOfVisuals();
        }

        return !areFieldsValid || !isVisualCountValid || hasIncomleteVisual || !areStatusesValid || !isNewCountValid;
    }

    private initForm(): void {
        this.form = this.formBuilder.group({
            media_title: [null],
            all_channels: [null],
            channel: [null],
            rule: [null],
            upload_img: [null],
            documents: [null]
        });
    }

    getCampaign() {
        this.campaignId = this.route.snapshot.paramMap.get('id');
        if (this.campaignId) {
            this.campaignService.getById(this.campaignId, true).subscribe(campaign => {
                this.campaign = campaign;
                this.numberOfVisuals = this.campaign.numberOfVisuals;
                this.newNumberOfVisuals = this.campaign.newNumberOfVisuals || 0;
                this.previousFiles = campaign.campaignDocuments || [];
                this.selectedFiles = [];

                // Same set as the old getCampaignChannels call (campaign.channels detail), now read
                // off the campaign response instead of a separate request.
                this.campaignChannels = (campaign.channelsDetails || []).filter(
                    (c: any) => (campaign.channels || []).includes(c.idChannel)
                );

                this.getAllChannelsByResolutions();
            });
        }
    }

    getAllChannelsByResolutions() {
        this.campaignHelperService.getAvailableLocations().subscribe((locationsRes: any) => {
            const removedChannels: number[] = [].concat(
                ...(this.campaign.removedProducts || []).map((p: any) => p.channels || [])
            );
            const allSelectedChannels = [
                ...(this.campaign.channels || []),
                ...removedChannels
            ];
            // Detect channels from the booked-channel details (resolved by id, so channels missing
            // from the location tree are still found), keeping the tree entry where present for rules.
            this.mediasData = this.campaignHelperService.groupBookedChannelsByResolution(
                locationsRes,
                (this.campaign as any).channelsDetails,
                allSelectedChannels
            );

            const result = this.mediaHelper.populateMediaList(this.campaign, this.mediasData);
            this.mediasList = result.mediasList;
            this.rulesArrayValues = result.rulesArrayValues;
            this.markStepPristine();
        });
    }

    public addSubMedia(pos: number) {
        this.mediaHelper.addSubMedia(this.mediasList, this.rulesArrayValues, pos);
    }

    public removeSubMedia(resPos: number, pos: number) {
        if (this.mediasList[resPos].subMedias[pos]) {
            this.removedSubMedias.push(this.mediasList[resPos].subMedias[pos]);
        }
        this.mediasList[resPos].subMedias.splice(pos, 1);
        this.rulesArrayValues[resPos].rules.splice(pos, 1);
    }

    public addChannel(resPos: number, pos: number) {
        this.mediasList[resPos].subMedias[pos].mediaChannels.push({ key: '', channelId: '', op: '', value: '' });
        if (!this.rulesArrayValues[resPos].rules[pos]) this.rulesArrayValues[resPos].rules[pos] = [];
        this.rulesArrayValues[resPos].rules[pos].push(null);
    }

    public removeChannel(resPos: number, subMediaIdx: number, channelIdx: number) {
        this.mediasList[resPos].subMedias[subMediaIdx].mediaChannels.splice(channelIdx, 1);
        this.rulesArrayValues[resPos].rules[subMediaIdx].splice(channelIdx, 1);
    }

    public updateNumberOfVisuals(count: number) {
        this.numberOfVisuals = count;
    }

    public updateNewNumberOfVisuals(count: number) {
        this.newNumberOfVisuals = count;
    }

    public getChannelType(channelId: string) {
        const channel = this.campaignChannels?.find(ch => ch.idChannel === channelId);
        if (channel?.multiScreensAsOne) return 'isMultiScreensAsOne';
        return this.campaignHelperService.getMultiResolution(channelId).length > 1 ? 'isMultiRes' : 'isNotMultiRes';
    }

    public getChannelsByResolutionKey(resolution: string) {
        return this.mediasData.get(resolution);
    }

    public getRulesByChannelIdAndResolution(resolution: string, channelId: string) {
        return this.mediaHelper.getRulesByChannelIdAndResolution(resolution, channelId, this.mediasData);
    }

    public onChangeChannel(resIdx, smIdx, chIdx, res, channelId) {
        const rules = this.getRulesByChannelIdAndResolution(res, channelId);
        const generic = rules.find(r => r.value == '-1');
        if (generic) {
            this.rulesArrayValues[resIdx].rules[smIdx][chIdx] = generic;
            const channel = this.mediasList[resIdx].subMedias[smIdx].mediaChannels[chIdx];
            channel.key = 'GEN'; channel.op = 'EQ'; channel.value = '-1';
        }
    }

    public onChangeRule(resIdx, smIdx, chIdx, event) {
        const channel = this.mediasList[resIdx].subMedias[smIdx].mediaChannels[chIdx];
        channel.key = event.value.ruleKey;
        channel.op = event.value.ruleOperatorEnum;
        channel.value = event.value.value;
    }

    public onImgFileChange(event: any, resIdx: number, smIdx: number, res: string, idGroup?: any, providedSubMedia?: any): void {
        this.uploadService.validateAndProcessMediaFile(event, res, (file) => {
            this.uploadingMedia = true;
            this.uploadService.uploadMedia(file).subscribe({
                next: (resData) => {
                    console.log('Media uploaded response:', resData);

                    if (idGroup !== undefined && idGroup !== null) {
                        resData.groupId = typeof idGroup === 'string' ? parseInt(idGroup, 10) : idGroup;
                        const assets = this.mediasList[resIdx].subMedias[smIdx].asset || [];
                        const idx = assets.findIndex(a => a.groupId == resData.groupId);
                        /* Force change detection by creating new array reference */
                        const newAssets = [...assets];
                        if (idx > -1) newAssets[idx] = resData; else newAssets.push(resData);
                        this.mediasList[resIdx].subMedias[smIdx].asset = newAssets;

                        // Also update the provided subMedia object if it exists (for dialog)
                        if (providedSubMedia) {
                            providedSubMedia.asset = newAssets;
                        }

                    } else {
                        // Standard upload mode: Replace any existing assets with the new one
                        const newAssets = [resData];
                        this.mediasList[resIdx].subMedias[smIdx].asset = newAssets;

                        if (providedSubMedia) {
                            providedSubMedia.asset = newAssets;
                        }
                    }

                    this.uploadingMedia = false;
                    this.cdr.detectChanges();

                },
                error: (err) => { this.alertService.error(err); this.uploadingMedia = false; this.cdr.detectChanges(); }
            });
        }, idGroup, this.mediasData);
    }

    public onDocumentsSelected = (event: any) => {
        const result = this.uploadService.handleDocumentSelection(event, this.selectedFiles);
        this.selectedFiles = result.files;
        this.documentsSizes = result.files.reduce((acc, f) => acc + f.size, 0);
        this.hasExceededMaxFileSize = result.exceeded;
    }

    public removeFile = (index: number) => {
        const result = this.uploadService.removeFile(index, this.selectedFiles);
        this.selectedFiles = result.files;
        this.documentsSizes = result.files.reduce((acc, f) => acc + f.size, 0);
        this.hasExceededMaxFileSize = false;
    }

    public removePreviousFile = (index: number) => {
        this.previousFiles.splice(index, 1);
    }

    public downloadDocument(fileName: string) {
        this.uploadService.downloadFile(fileName);
    }

    public downloadMedia(fileName: string) {
        this.uploadService.downloadFile(fileName);
    }

    public async onFileTypeSelect(type: 'excel' | 'pdf') {
        // The plan is generated server-side from the saved campaign, so the whole step is
        // persisted first — visuals, documents and both visual counts — otherwise the file
        // comes back built from whatever was last written rather than what is on screen.
        if (!this.isValidNumberOfVisuals()) {
            this.alertService.error('Enter the number of visuals before downloading the media plan.',
                { id: 'save-campaign', autoClose: true });
            return;
        }

        this.selectedFileType = type;
        this.loading = true;

        const saved = await this.saveDraft();
        if (!saved) {
            // saveDraft has already reported why; don't hand back a plan built from stale data.
            this.loading = false;
            this.selectedFileType = null;
            return;
        }

        const apiCall = type === 'pdf'
            ? this.campaignProposal.downloadAsPDF(this.campaignId)
            : this.campaignProposal.downloadAsExcel(this.campaignId);

        apiCall.subscribe(res => {
            this.uploadService.createDownloadLink(res, `proposal.${type === 'pdf' ? 'pdf' : 'xlsx'}`);
            this.loading = false; this.selectedFileType = null;
        }, () => {
            this.loading = false; this.selectedFileType = null;
            this.alertService.error('Could not download the media plan. Please try again.',
                { id: 'save-campaign', autoClose: true });
        });
    }

    public getAssetByIdGroup(asset: any[], idGroup: any) {
        if (!asset || !Array.isArray(asset)) return null;
        // Normalize groupId to integer for proper comparison
        const normalizedIdGroup = typeof idGroup === 'string' ? parseInt(idGroup, 10) : idGroup;
        const found = asset.find(a => {
            const assetGroupId = typeof a.groupId === 'string' ? parseInt(a.groupId, 10) : a.groupId;
            return assetGroupId === normalizedIdGroup;
        });
        return found ? found.fileName : null;
    }

    public openUploadMediaDialog(idChannel, subMedia, subMediaIdx, resIdx, actionType, channelType): void {
        const multiScreenChannel = channelType == 'isMultiScreensAsOne' && this.campaignChannels?.find(ch => ch.idChannel === idChannel);
        const multiResolution = this.campaignHelperService.getMultiResolution(idChannel);
        this.dialog.open(this.uploadImageDialog, {
            width: '600px', height: '600px',
            data: { multiResolution, subMedia, subMediaIdx, resIdx, actionType, multiScreenChannel }
        });
    }

    public pauseCampaignVisual(subMedia: any) {
        const channelEndDate = getProductEndDateByChannelId(this.campaign, subMedia.mediaChannels[0].channelId);
        if (isLessThan2WorkingDays(channelEndDate)) {
            this.dialog.open(WarningMessageModalComponent, { data: { title: `Campaign ends soon. Contact support to pause.` } });
        } else {
            this.dialog.open(PauseCampaignVisualModalComponent, {
                width: '600px', data: { subMedia, campaignId: this.campaign._id }
            // Mark it paused here rather than reloading the campaign, which would drop unsaved edits
            }).afterClosed().subscribe(res => { if (res) subMedia.status = 'PAUSED'; });
        }
    }

    public saveCampaign() {
        this.saveDraft().then(() => {
            this.getCampaign();
            this.removedSubMedias = [];
        });
    }

    public async saveDraft(): Promise<boolean> {
        if (this.isReadOnlyFlow()) {
            return true;
        }

        this.alertService.clear();

        try {
            this.loadingOverlayService.show();
            this.mediaHelper.prepareProductMedia(this.campaign, this.mediasList, true);
            await this.campaignMediaService.uploadDocuments(this.campaign._id, this.previousFiles, this.selectedFiles).toPromise();
            this.campaign.numberOfVisuals = this.numberOfVisuals;
            this.campaign.newNumberOfVisuals = this.newNumberOfVisuals;
            const updatedCampaign = await this.campaignService.update(this.campaign._id, this.campaign).toPromise();
            this.campaign = updatedCampaign; // Sync local campaign with backend response (IDs, etc.)

            // Re-populate mediasList so it references the new objects in updatedCampaign and has correct IDs
            if (this.mediasData) {
                const result = this.mediaHelper.populateMediaList(this.campaign, this.mediasData);
                this.mediasList = result.mediasList;
                this.rulesArrayValues = result.rulesArrayValues;
            }

            this.loadingOverlayService.hide();
            this.markStepPristine();
            this.alertService.success('Draft saved successfully.', { id: 'save-campaign', autoClose: true });
            return true;
        } catch (error) {
            this.loadingOverlayService.hide();
            console.error('Error saving draft:', error);
            this.alertService.error('Error saving draft. Please try again or contact support.', { id: 'save-campaign', autoClose: true });
            return false;
        }
    }

    getExitMessage(): string {
        return "Make sure to fill the visuals details. Otherwise, your incomplete visuals will be discarded.";
    }

    /**
     * Leaving step 4 always confirms, whether or not anything was edited — the client is asked
     * to discard, save, or stay. Deliberately not gated on {@link hasStepChanges}: that only
     * governs whether the Save button is enabled.
     *
     * Preview, read-only and already-submitted flows have nothing to lose, so they pass through.
     */
    shouldConfirmBeforeLeaving(): boolean {
        return !(this.isFormSubmitted || this.previewMode || !this.isClient());
    }

    canDeactivate(): any {
        if (this.isFormSubmitted || this.previewMode || !this.isClient()) {
            return true;
        }
        if (RulesHelper.hasAnyIncompleteVisual(this.mediasList)) {
            return false;
        }
        if (RulesHelper.hasAtLeastOneCompleteVisual(this.mediasList)) {
            return this.saveDraft();
        }
        return true;
    }

    public navigateNextStep() {
        if (this.isReadOnlyFlow()) {
            return;
        }

        this.isFormSubmitted = true;
        if (this.checkFieldsValidation()) return;
        this.loadingOverlayService.show();
        this.mediaHelper.prepareProductMedia(this.campaign, this.mediasList);
        this.campaignMediaService.uploadDocuments(this.campaign._id, this.previousFiles, this.selectedFiles).subscribe(() => {
            this.campaign.numberOfVisuals = this.numberOfVisuals;
            this.campaign.newNumberOfVisuals = this.newNumberOfVisuals;
            this.campaignService.update(this.campaign._id, this.campaign).subscribe(data => {
                this.campaign = data;
                this.loadingOverlayService.hide();
                this.dialog.open(ConfirmCampaignCreationModalComponent, {
                    width: '420px', data: { campaign: this.campaign, editMode: this.editMode, liveEditMode: this.liveEditMode, partialEditMode: this.partialEditMode, onCancel: this.getCampaign.bind(this) }
                });
            });
        }, () => this.loadingOverlayService.hide());
    }

    public getChannelStatusForMedia(media): ChannelStatus {
        return this.mediaHelper.getChannelStatus(this.campaign, media);
    }

    public showChannelStatusDistinction(): boolean {
        return showChannelStatusDistinction(this.campaign.status, this.authenticationService.getSessionRole());
    }

    public validateGenericRule(resIdx, smIdx, chId, chIdx, rule) {
        return RulesHelper.validateGenericRule(this.mediasList, resIdx, smIdx, chId, chIdx, rule);
    }

    public isVideo(filename: string): boolean {
        if (!filename) { return false; }
        const ext = filename.split('.').pop().toLowerCase();
        return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', 'mkv'].includes(ext);
    }

    public validateOnlyOneChannelPerMedia(resIdx, smIdx, chId) {
        return RulesHelper.validateOnlyOneChannelPerMedia(this.mediasList, resIdx, smIdx, chId);
    }
}